import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { UsernamePasswordCredential } from '../models/credential/username-password-credential.model';
import { CertificateCredential } from "../models/credential/certificate-credential.model";
import { CredentialListItem, GeneralCredentialListItem } from "../models/credential/credential-list-item.model";
import { JurisdictionObligationCredentialCreationInfo } from "../models/credential/jurisdiction-obligation-credential-creation-info.model";
import { JurisdictionObligationCredentialLink } from "../models/credential/jurisdiction-obligation-credential-link.model";
import { tap } from "rxjs/operators";
import { CredentialBase } from "../models/credential/credential-base.model";
import { UsernamePasswordWithTokenCredential } from "../models/credential/username-password-token-credential.model";
import { VatUkTestUser } from "../models/credential/vat-uk-test-user.model";
import { VatUkObligation } from "../models/credential/vat-uk-obligation.model";
import { ApplicationSettings } from "projects/lib_config/src/public-api";
import { CredentialType } from "../models/credential/credential-type.enum";

@Injectable()
export class CredentialService {
  readonly saveCredentialFunctionsByType: { credentialType: string, saveCred: (credential: CredentialBase) => Observable<string>,  saveCredLink: (credentialLink: JurisdictionObligationCredentialLink) => Observable<string>  }[] =
    [{
      credentialType: CredentialType.CERTIFICATE,
      saveCred: cred => this.saveCertificateCredential(cred as CertificateCredential),
      saveCredLink:  credLink => this.saveCredentialLinkWithCertificate(credLink),
    },
    {
      credentialType: CredentialType.USERNAME_PASSWORD,
      saveCred: cred => this.saveUserNameAndPasswordCredential(cred as UsernamePasswordCredential),
      saveCredLink: credLink => this.saveCredentialLinkWithUsernamePassword(credLink),
    },
    {
      credentialType: CredentialType.USERNAME_PASSWORD_TOKEN,
      saveCred: cred => this.saveUserNameAndPasswordWithTokenCredential(cred as UsernamePasswordWithTokenCredential),
      saveCredLink: credLink => this.saveCredentialLinkWithUsernamePasswordToken(credLink),
    }];

  constructor(private http: HttpClient, private settings: ApplicationSettings) {
  }

  getJurisdictionObligationCredentialCreationInfo(jurisdictionId: string): Observable<JurisdictionObligationCredentialCreationInfo> {
    return this.http.get<JurisdictionObligationCredentialCreationInfo>(this.settings.backendUrlMasterData + `/credentials/judObjCredCreationInfo/${jurisdictionId}`);
  }

  getJurisdictionObligationCredentialLink(linkId: string): Observable<JurisdictionObligationCredentialLink> {
    return this.http.get<JurisdictionObligationCredentialLink>(this.settings.backendUrlMasterData + `/credentials/credentiallink/${linkId}`);
  }

  getAllGeneralCredentialListItem(): Observable<GeneralCredentialListItem[]> {
    return this.http.get<GeneralCredentialListItem[]>(this.settings.backendUrlMasterData + `/credentials/general/all`).pipe(tap(x => x?.forEach(y => y.isGeneral = true)));
  }

  getAllCompanyCredentialListItem(jurisdictionObligationIds: string[]): Observable<CredentialListItem[]> {
    return this.http.post<CredentialListItem[]>(this.settings.backendUrlMasterData + `/credentials/all`, { jurisdictionObligationIds: jurisdictionObligationIds });
  }

  getGeneralCredentialListItemByJurisdictionAndTypes(jurisdiction: string, returnTypes: string[]): Observable<CredentialListItem[]> {
    let params: HttpParams = new HttpParams();
    if (jurisdiction) params = params.set('jurisdiction', jurisdiction);
    if (returnTypes) returnTypes.forEach(returnType => { params = params.append('returnTypes', returnType); });

    return this.http.get<CredentialListItem[]>(this.settings.backendUrlMasterData + `/credentials/general`, { params: params });
  }

  getCredentialById(id: string): Observable<CredentialBase> {
    return this.http.get<CredentialBase>(this.settings.backendUrlMasterData + `/credentials/${id}`);
  }

  getCredentialSecret(credentialId: string, uri: string) {
    let params: HttpParams = new HttpParams();
    if (uri) params = params.set('secretUri', uri);
    return this.http.get<string>(this.settings.backendUrlMasterData + `/credentials/${credentialId}/secret`, { params: params });
  }

  getVatUkObligations(vatNumber: string, credentialId: string): Observable<VatUkObligation[]> {
    return this.http.get<VatUkObligation[]>(this.settings.backendUrlMasterData + `/jurisdictionobligation/uk/${vatNumber}/${credentialId}`)
  }

  getVatNumberByCredentialLinkId(id: string): Observable<string> {
    return this.http.get<string>(this.settings.backendUrlMasterData + `/credentials/credentiallink/vatnumber/${id}`);
  }

  saveCredentialLink(credentialLink: JurisdictionObligationCredentialLink) {
    let saveCredFunc = this.saveCredentialFunctionsByType.find(x => x.credentialType === credentialLink.credential.type);
    if (saveCredFunc === null) throw "Unknown Credential type in credential service function 'CreateCredentialByType'";

    //add LinkId/credId because Id is used by something else in backend....
    let saveCredentialLink = Object.assign({}, credentialLink);
    if (saveCredentialLink) (<any>saveCredentialLink).linkId = credentialLink.id;
    if (saveCredentialLink?.credential) (<any>saveCredentialLink.credential).credId = credentialLink.credentialId;

    return saveCredFunc.saveCredLink(saveCredentialLink);
  }

  saveCredentialLinkWithCertificate(credentialLink: JurisdictionObligationCredentialLink) {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/credentials/credentiallink/certificate`, credentialLink)
  }

  saveCredentialLinkWithUsernamePassword(credentialLink: JurisdictionObligationCredentialLink) {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/credentials/credentiallink/usernameandpassword`, credentialLink)
  }

  saveCredentialLinkWithUsernamePasswordToken(credentialLink: JurisdictionObligationCredentialLink) {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/credentials/credentiallink/usernamepasswordtoken`, credentialLink)
  }

  deleteCredentialLink(linkId: string) {
    return this.http.delete(this.settings.backendUrlMasterData + `/credentials/credentiallink/${linkId}`);
  }

  createVatUkTestUser(jurisdictionObligationId: string) {
    let params: HttpParams = new HttpParams();
    if (jurisdictionObligationId) params = params.set('jurisdictionObligationId', jurisdictionObligationId);
    return this.http.post<VatUkTestUser>(this.settings.backendUrlMasterData + `/credentials/vatUk/testUser`, null, { params: params })
  }

  saveCredentialByType(credential: CredentialBase) {
    let saveCredFunc = this.saveCredentialFunctionsByType.find(x => x.credentialType === credential.type);
    if (saveCredFunc === null) throw "Unknown Credential type in credential service function 'CreateCredentialByType'";

    //add credId because Id is used by something else in backend....
    let saveCredential = Object.assign({}, credential);
    if (saveCredential) (<any>saveCredential).credId = credential.id;

    return saveCredFunc.saveCred(saveCredential);
  }

  saveUserNameAndPasswordCredential(credential: UsernamePasswordCredential) {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/credentials/usernameandpassword`, credential)
  }

  saveUserNameAndPasswordWithTokenCredential(credential: UsernamePasswordWithTokenCredential) {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/credentials/usernamepasswordtoken`, credential)
  }

  saveCertificateCredential(credential: CertificateCredential) {
    return this.http.post<string>(this.settings.backendUrlMasterData + `/certificate`, credential)
  }

  deleteCredential(id: string) {
    return this.http.delete(this.settings.backendUrlMasterData + `/credentials/${id}`)
  }
}